import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { getApiErrorMessage } from "@/api/client";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { useStore } from "@/store";
import { trackEvent } from "@/utils/trackEvent";

import { RemovePermissionButton } from "./RemovePermissionButton";

import type { DocumentPermission } from "@/api";
import type { DropdownItem } from "@/components/sidebar/Dropdown";

export const PermissionDropdown = observer(function PermissionDropdown({
  permission,
  onError,
}: {
  permission: DocumentPermission;
  onError?: (error: string) => void;
}) {
  const t = useTranslations();
  const { doc } = useStore();
  const { scope } = permission;

  if (!doc) {
    return null;
  }

  const identityLabel = permission.team?.permalink ?? permission.email ?? permission.uid;

  const items: DropdownItem[] = [
    {
      title: t("share.canEditAndComment"),
      active: scope === "write",
      onClick: async () => {
        try {
          await doc.permissions.updatePermission(permission, "write");
          if (doc.id) {
            trackEvent("share_user_permission_setting_changed", {
              setting: t("share.canEditAndComment"),
              username: identityLabel,
              doc_id: doc.id,
            });
          }
        } catch (err) {
          onError?.(getApiErrorMessage(err));
        }
      },
    },
    {
      title: t("share.canViewAndComment"),
      active: scope === "comment",
      onClick: async () => {
        try {
          await doc.permissions.updatePermission(permission, "comment");
          if (doc.id) {
            trackEvent("share_user_permission_setting_changed", {
              setting: t("share.canViewAndComment"),
              username: identityLabel,
              doc_id: doc.id,
            });
          }
        } catch (err) {
          onError?.(getApiErrorMessage(err));
        }
      },
    },
    {
      title: t("share.canViewOnly"),
      active: scope === "read",
      onClick: async () => {
        try {
          await doc.permissions.updatePermission(permission, "read");
          if (doc.id) {
            trackEvent("share_user_permission_setting_changed", {
              setting: t("share.canViewOnly"),
              username: identityLabel,
              doc_id: doc.id,
            });
          }
        } catch (err) {
          onError?.(getApiErrorMessage(err));
        }
      },
    },
    {
      component: ({ close }) => (
        <RemovePermissionButton
          onClick={async () => {
            try {
              await doc.permissions.deletePermission(permission.uid);
              if (doc.id) {
                trackEvent("share_user_permission_setting_changed", {
                  setting: t("share.remove"),
                  username: identityLabel,
                  doc_id: doc.id,
                });
              }
              close();
            } catch (err) {
              onError?.(getApiErrorMessage(err));
            }
          }}
        />
      ),
    },
  ];

  let toggleText = "";

  if (scope === "write") {
    toggleText = t("share.canEdit");
  } else if (scope === "comment") {
    toggleText = t("share.canComment");
  } else if (scope === "read") {
    toggleText = t("share.canView");
  }

  return (
    <Dropdown
      anchor="bottom end"
      toggleText={toggleText}
      toggleTextColor="text-document-text"
      toggleTextInvertedBg
      items={items}
    />
  );
});
